import { useQueryHelper } from '@anytag/shared-hooks';
import { checkIsReelPost } from '@anytag/shared-utils';
import { POST_COMMENTS_LIMIT } from '@anytag/shared-constants';
import { PostDetailStatistics, SupportedSocialMediaType } from '@anytag/shared-types';
import { CampaignSocialMediaType, useMarketplacePostQuery, useMarketplacePostCommentsQuery } from '@anytag/graphql';
import { PostModal, usePostModalContext } from '../shared';
import { MarketplacePostStatus } from './marketplace-post-status';

export const DetailsTemplate = () => {
  const { t, enqueueSnackbar } = useQueryHelper();
  const { closeModal, selectedPostId } = usePostModalContext();
  const { data, loading } = useMarketplacePostQuery({
    skip: !selectedPostId,
    fetchPolicy: 'cache-and-network',
    variables: { pk: Number(selectedPostId) },
    onError: (error) => {
      closeModal?.();
      enqueueSnackbar(t(error.message), { variant: 'error' });
    }
  });
  const { data: commentData, loading: fetchingComments } = useMarketplacePostCommentsQuery({
    skip: !selectedPostId,
    fetchPolicy: 'cache-and-network',
    variables: { postId: Number(selectedPostId), limit: POST_COMMENTS_LIMIT }
  });

  const details = data?.marketplacePost;
  const statistics: PostDetailStatistics = (() => {
    switch (details?.socialMedia) {
      case CampaignSocialMediaType.INSTAGRAM:
        return { likes: details?.likes, reach: details?.reach, comments: details?.comments };
      case CampaignSocialMediaType.INSTAGRAM_STORY:
        return { likes: details?.likes, reach: details?.reach, replies: details?.comments };
      case CampaignSocialMediaType.YOUTUBE:
        return { likes: details?.likes, views: details?.views, comments: details?.comments };
      case CampaignSocialMediaType.TWITTER:
        return { likes: details?.likes, retweets: details?.shares, replies: details?.comments };
      case CampaignSocialMediaType.THREADS:
        return { likes: details?.likes, replies: details?.comments };
      case CampaignSocialMediaType.FACEBOOK:
        return { reactions: details?.likes, comments: details?.comments, shares: details?.shares };
      case CampaignSocialMediaType.TIKTOK:
        return {
          likes: details?.likes,
          views: details?.views,
          comments: details?.comments,
          shares: details?.shares
        };
      default:
        return {};
    }
  })();

  return (
    <PostModal.DetailsTemplate
      loading={loading || fetchingComments}
      postStatus={
        details?.status ? (
          <MarketplacePostStatus
            status={details?.status}
            className="h-4 text-xs"
            warningReason={details.warningReason || undefined}
          />
        ) : undefined
      }
      data={
        details
          ? {
              statistics,
              caption: details?.caption,
              postUrl: details?.postUrl,
              postDate: details?.postedDate,
              images: details?.images || [],
              socialMedia: details?.socialMedia as keyof typeof SupportedSocialMediaType, // we don't support type OTHERS, UNSELECT, ...
              isReel: checkIsReelPost(details?.postUrl),
              comments: commentData?.marketplacePostComments || [],
              info: {
                id: String(details?.postDetailInfluencer?.id ?? ''),
                name: details?.postDetailInfluencer?.name,
                avatar: details?.postDetailInfluencer?.avatar,
                followers: details?.postDetailInfluencer?.followersCount
              }
            }
          : undefined
      }
    />
  );
};
